import { useState } from "react";
import { AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { deleteAccount, clearAllLocalData } from "@/services/unsaidService";

export function DeleteAccountDialog({ open, onOpenChange }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const reset = () => {
    setPassword(""); setConfirm(""); setError(""); setBusy(false); setDone(false);
  };

  const handleOpenChange = (v) => {
    if (busy) return;          // never close mid-deletion
    if (!v) reset();
    onOpenChange(v);
  };

  const canDelete = password.trim().length > 0 && confirm.trim() === "DELETE" && !busy;

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true); setError("");
    try {
      await deleteAccount(password, confirm.trim());
      // Wipe every trace of this account from the device. Keep the auth context
      // populated for now so the route guard doesn't unmount us before the
      // confirmation renders — we drop the session on "Return to FioNova".
      clearAllLocalData();
      setDone(true);
    } catch (e) {
      setBusy(false);
      setError(e.message || "We couldn't delete your account. Please try again.");
    }
  };

  const returnHome = () => {
    // The account and its token are already gone (clearAllLocalData ran after a
    // successful delete). A hard navigation to the public home boots the app
    // fresh as a guest and avoids any SPA route-guard redirect races.
    window.location.replace("/");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="delete-account-dialog" className="max-w-md rounded-3xl">
        {done ? (
          <div className="flex flex-col items-center gap-4 py-4 text-center" data-testid="delete-account-confirmation">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <ShieldCheck className="h-6 w-6" strokeWidth={1.5} />
            </span>
            <DialogHeader className="items-center">
              <DialogTitle className="text-center">Your account has been deleted</DialogTitle>
              <DialogDescription className="text-center">
                Your FioNova account and associated personal content have been permanently removed. Take good care.
              </DialogDescription>
            </DialogHeader>
            <button
              data-testid="delete-account-return-home"
              onClick={returnHome}
              className="mt-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
              Return to FioNova
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <span className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <DialogTitle>Delete My Account &amp; Data</DialogTitle>
              <DialogDescription>
                Permanently delete your FioNova account and associated personal content. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <p className="text-sm leading-relaxed text-muted-foreground">
              This removes your Companion Memory, journal entries, goals, saved reflections, and journey history.
              If you have an active subscription, it will be cancelled first so you're never charged again.
              This is separate from cancelling your subscription — it deletes everything.
            </p>

            <div className="mt-1 space-y-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="del-pw">
                  Confirm your password
                </label>
                <input
                  id="del-pw"
                  data-testid="delete-account-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your current password"
                  className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base text-foreground outline-none focus:border-destructive focus:ring-2 focus:ring-destructive/30"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="del-confirm">
                  Type <span className="font-semibold text-destructive">DELETE</span> to confirm
                </label>
                <input
                  id="del-confirm"
                  data-testid="delete-account-confirm-text"
                  type="text"
                  autoComplete="off"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="DELETE"
                  className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base text-foreground outline-none focus:border-destructive focus:ring-2 focus:ring-destructive/30"
                />
              </div>
            </div>

            {error && (
              <p data-testid="delete-account-error" className="text-sm text-destructive">{error}</p>
            )}

            <div className="mt-2 flex flex-col gap-2">
              <button
                data-testid="delete-account-confirm-button"
                onClick={doDelete}
                disabled={!canDelete}
                className="flex items-center justify-center gap-2 rounded-full bg-destructive px-6 py-3 text-sm font-semibold text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-40">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy ? "Deleting…" : "Permanently Delete My Account"}
              </button>
              <button
                data-testid="delete-account-cancel-button"
                onClick={() => handleOpenChange(false)}
                disabled={busy}
                className="rounded-full px-6 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40">
                Cancel
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
